import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { Api } from "../../api/Api";
import { TipManager } from "../../tips/TipManager";
import {
  TipStorage,
  TIPS_ENABLED_CHANGED_EVENT,
} from "../../tips/TipStorage";
import { designTokensCss } from "./designTokens";
import "./SugarFpsMonitor";
import { logger } from "../../util/Logger";
import i18next from "i18next";
import { t } from "../../translation/i18n";
import { Engine } from "../../engine/Engine";
import type { SugarFpsMonitor } from "./SugarFpsMonitor";
import {
  RENDER_SETTINGS_CHANGED_EVENT,
  renderSettingsStore,
  type RenderSettings,
  type ShadowQuality,
  type SsgiOutputMode,
  type SsgiSparseAdaptive,
  type SsgiSparseEdgeKernel,
  type SsgiSparseOutputMode,
  type SsgiSparseVersion,
  type ToneMappingName,
} from "../pipeline/RenderSettingsStore";
import {
  getLengthUnit,
  LENGTH_UNIT_CHANGED_EVENT,
  lengthUnitStore,
} from "../units/LengthUnit";
import {
  isAabbDebugDrawEnabled,
  isAabbDebugUrlForced,
  isCornerLineSnapEnabled,
  isDragCollisionDebugEnabled,
  isDragCollisionDebugUrlForced,
  isObbDebugDrawEnabled,
  isObbDebugUrlForced,
  setAabbDebugDrawEnabled,
  setCornerLineSnapEnabled,
  setDragCollisionDebugEnabled,
  setObbDebugDrawEnabled,
  setVolumeDebugColor,
  volumeDebugColorHex,
  VOLUME_DEBUG_CHANGED_EVENT,
  type VolumeDebugColorRole,
} from "../../util/DebugFlags";

/** Room Designer Live/Dev preference. Default is Live (stats hidden). */
export const ROOM_DESIGNER_DEV_MODE_KEY = "sugartech:room-designer:dev-mode";
export const DEV_MODE_CHANGED_EVENT = "dev-mode-changed";

export function isRoomDesignerDevMode(): boolean {
  try {
    return localStorage.getItem(ROOM_DESIGNER_DEV_MODE_KEY) === "true";
  } catch {
    return false;
  }
}

export function setRoomDesignerDevMode(enabled: boolean): void {
  try {
    localStorage.setItem(ROOM_DESIGNER_DEV_MODE_KEY, String(enabled));
  } catch {
    /* ignore quota / private mode */
  }
}

export const settingsMenuCss = css /*css*/`
    ${designTokensCss}

  :host {
    display: block;
    position: relative;
    overflow: visible;
    pointer-events: auto;
  }

  :host([settings-open]) {
    z-index: 10049;
  }

  .settings-button {
    position: relative;
    width: var(--srd-tool-size);
    height: var(--srd-tool-size);
    padding: 0;
    border-radius: var(--srd-radius-sm);
    background: var(--srd-surface);
    backdrop-filter: var(--srd-blur);
    -webkit-backdrop-filter: var(--srd-blur);
    border: 1px solid var(--srd-border);
    box-shadow: var(--srd-shadow-md);
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    font: inherit;
    color: var(--srd-text-muted);
    transition: background var(--srd-transition), color var(--srd-transition),
      box-shadow var(--srd-transition);
  }

  .settings-button:hover {
    background: var(--srd-surface-hover);
    color: var(--srd-text);
    box-shadow: var(--srd-shadow-lg);
  }

  .settings-button:focus-visible {
    outline: none;
    box-shadow: var(--srd-focus-ring);
  }

  .settings-popup {
    position: absolute;
    right: 0;
    bottom: calc(100% + var(--srd-space-3));
    width: min(320px, calc(100vw - 2 * var(--srd-space-5)));
    background: var(--srd-surface-solid);
    border: 1px solid var(--srd-border);
    border-radius: var(--srd-radius-md);
    box-shadow: var(--srd-shadow-lg);
    padding: var(--srd-space-4);
    z-index: 1000;
    display: none;
    font-family: var(--srd-font);
    color: var(--srd-text);
    max-height: min(70vh, 560px);
    overflow-y: auto;
    box-sizing: border-box;
  }

  /* Bottom-left controls: grow up + inward (to the right). */
  :host([popup-placement="above-start"]) .settings-popup {
    left: 0;
    right: auto;
  }

  /* Anchor to the gear host. Do not use position:fixed + viewport coords:
     chrome transform / backdrop-filter become the containing block and
     park the panel away from the icon (same class of bug as the filter dock). */
  :host([popup-placement="below"]) .settings-popup,
  :host([popup-placement="below-end"]) .settings-popup {
    position: absolute;
    top: calc(100% + var(--srd-space-3));
    bottom: auto;
    margin: 0;
    z-index: 10050;
  }

  :host([popup-placement="below"]) .settings-popup {
    left: 0;
    right: auto;
  }

  :host([popup-placement="below-end"]) .settings-popup {
    left: auto;
    right: 0;
  }

  :host([popup-placement="below"]) .settings-popup.active,
  :host([popup-placement="below-end"]) .settings-popup.active {
    animation: srdFadeInBelow 0.2s ease-out;
  }

  .settings-popup.active {
    display: block;
    animation: srdFadeIn 0.2s ease-out;
  }

  @keyframes srdFadeIn {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes srdFadeInBelow {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .settings-popup h3 {
    margin: 0 0 var(--srd-space-4);
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: var(--srd-text);
  }

  .settings-group {
    margin: 0 0 var(--srd-space-3);
    padding: var(--srd-space-1) var(--srd-space-3) var(--srd-space-2);
    border: 1px solid var(--srd-border-strong);
    border-radius: var(--srd-radius-md);
  }

  .settings-group-display {
    background: #eef2f6;
  }

  .settings-group-environment {
    background: #f6f3ec;
  }

  .settings-group-advanced {
    background: #eef4f1;
  }

  .settings-group-nested {
    background: rgba(255, 255, 255, 0.62);
  }

  .settings-group-help {
    background: #f4f1f6;
  }

  .settings-group-debug {
    background: #f7f2ee;
  }

  .settings-group > .settings-section-title:first-child {
    margin-top: var(--srd-space-2);
  }

  .settings-group .settings-item:last-child,
  .settings-group .settings-range:last-child {
    border-bottom: none;
  }

  .settings-section-title {
    margin: var(--srd-space-3) 0 var(--srd-space-1);
    color: var(--srd-text-muted);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0;
  }

  .settings-advanced-toggle {
    appearance: none;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 0 var(--srd-space-3);
    padding: var(--srd-space-3);
    border: 1px solid var(--srd-border-strong);
    border-radius: var(--srd-radius-md);
    background: #e7eeea;
    color: var(--srd-text);
    font: inherit;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    text-align: left;
  }

  .settings-advanced-toggle:focus-visible {
    outline: none;
    box-shadow: var(--srd-focus-ring);
  }

  .settings-hdr-preview {
    display: block;
    width: 100%;
    height: 72px;
    margin: 0 0 var(--srd-space-2);
    object-fit: cover;
    border-radius: var(--srd-radius-sm);
    background: var(--srd-bg-muted);
  }

  .settings-block {
    margin: var(--srd-space-2) 0;
    padding: var(--srd-space-3);
    border: 1px solid var(--srd-border);
    border-radius: var(--srd-radius-md);
    background: var(--srd-bg);
  }

  .settings-block.is-active {
    border-color: rgba(0, 149, 135, 0.35);
    background: var(--srd-accent-soft);
  }

  .settings-block.is-disabled {
    opacity: 0.48;
    pointer-events: none;
  }

  .settings-block-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--srd-space-3);
    margin-bottom: var(--srd-space-1);
  }

  .settings-block-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--srd-text);
  }

  .settings-hint {
    margin: 0 0 var(--srd-space-2);
    font-size: 0.75rem;
    line-height: 1.35;
    color: var(--srd-text-muted);
  }

  .settings-range {
    display: grid;
    gap: var(--srd-space-2);
    padding: var(--srd-space-3) 0;
    border-bottom: 1px solid var(--srd-border);
  }

  .settings-range-meta {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--srd-space-3);
    font-size: 0.8125rem;
  }

  .settings-range output {
    color: var(--srd-text-muted);
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.6875rem;
  }

  .settings-range input[type="range"] {
    width: 100%;
    margin: 0;
    accent-color: var(--srd-accent);
    cursor: pointer;
  }

  .settings-actions {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--srd-space-2);
    padding-top: var(--srd-space-3);
  }

  .settings-reset-btn {
    width: 100%;
    padding: var(--srd-space-3);
    background: transparent;
    color: var(--srd-text);
    border: 1px solid var(--srd-border-strong);
    border-radius: var(--srd-radius-sm);
    font-size: 0.8125rem;
    font-family: var(--srd-font);
    font-weight: 600;
    cursor: pointer;
    transition: background-color var(--srd-transition),
      border-color var(--srd-transition), color var(--srd-transition);
  }

  .settings-reset-btn:hover {
    background: var(--srd-bg-muted);
    border-color: var(--srd-text-muted);
  }

  .settings-reset-btn:focus-visible {
    outline: none;
    box-shadow: var(--srd-focus-ring);
  }

  .settings-popup-content {
    display: flex;
    flex-direction: column;
    gap: var(--srd-space-1);
  }

  .settings-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--srd-space-3) 0;
    border-bottom: 1px solid var(--srd-border);
    font-size: 0.875rem;
    color: var(--srd-text);
  }

  .settings-item:last-child {
    border-bottom: none;
  }

  .settings-item select {
    padding: var(--srd-space-2) var(--srd-space-3);
    border-radius: var(--srd-radius-sm);
    border: 1px solid var(--srd-border-strong);
    outline: none;
    font-size: 0.875rem;
    font-family: var(--srd-font);
    background: var(--srd-surface-solid);
    color: var(--srd-text);
    min-width: 150px;
    transition: border-color var(--srd-transition), box-shadow var(--srd-transition);
  }

  .settings-item select:focus {
    border-color: var(--srd-accent);
    box-shadow: var(--srd-focus-ring);
  }

  .settings-item input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: var(--srd-accent);
  }

  .settings-volume-controls {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .settings-item input[type="color"] {
    width: 28px;
    height: 22px;
    padding: 0;
    border: 1px solid var(--srd-border-strong);
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
  }

  .settings-segment {
    display: inline-flex;
    align-items: stretch;
    border: 1px solid var(--srd-border-strong);
    border-radius: var(--srd-radius-sm);
    overflow: hidden;
    background: var(--srd-bg);
  }

  .settings-segment-btn {
    appearance: none;
    margin: 0;
    padding: var(--srd-space-2) var(--srd-space-3);
    border: none;
    background: transparent;
    color: var(--srd-text-muted);
    font: inherit;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color var(--srd-transition), color var(--srd-transition);
  }

  .settings-segment-btn + .settings-segment-btn {
    border-left: 1px solid var(--srd-border-strong);
  }

  .settings-segment-btn:hover {
    color: var(--srd-text);
    background: var(--srd-bg-muted);
  }

  .settings-segment-btn.active {
    color: var(--srd-button-text);
    background: var(--srd-button);
  }

  .settings-segment-btn:focus-visible {
    outline: none;
    box-shadow: inset var(--srd-focus-ring);
  }

  .settings-item img {
    width: 40px;
    height: 40px;
    border-radius: var(--srd-radius-sm);
    object-fit: cover;
  }

  .hdr-option {
    display: flex;
    align-items: center;
    gap: var(--srd-space-2);
  }

  .hdr-option img {
    width: 30px;
    height: 30px;
    border-radius: var(--srd-radius-sm);
    object-fit: cover;
  }

  .settings-restart-btn {
    padding: var(--srd-space-2) var(--srd-space-3);
    background-color: var(--srd-button);
    color: var(--srd-button-text);
    border: none;
    border-radius: var(--srd-radius-sm);
    font-size: 0.8125rem;
    font-family: var(--srd-font);
    font-weight: 500;
    cursor: pointer;
    transition: background-color var(--srd-transition);
  }

  .settings-restart-btn:hover {
    background-color: var(--srd-button-hover);
  }

  .settings-restart-btn:focus-visible {
    outline: none;
    box-shadow: var(--srd-focus-ring);
  }

  .settings-nav-btn {
    appearance: none;
    width: 100%;
    display: flex;
    align-items: center;
    gap: var(--srd-space-3);
    margin: 0 0 var(--srd-space-3);
    padding: var(--srd-space-3);
    border: 1px solid var(--srd-border-strong);
    border-radius: var(--srd-radius-md);
    background: var(--srd-bg);
    color: var(--srd-text);
    font: inherit;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    text-align: left;
    transition:
      background-color var(--srd-transition),
      border-color var(--srd-transition),
      color var(--srd-transition);
  }

  .settings-nav-btn:hover {
    background: var(--srd-bg-muted);
    border-color: var(--srd-accent);
    color: var(--srd-accent);
  }

  .settings-nav-btn:focus-visible {
    outline: none;
    box-shadow: var(--srd-focus-ring);
  }

  .settings-nav-btn-icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    display: block;
  }

  .settings-nav-btn-label {
    flex: 1 1 auto;
    min-width: 0;
  }

  .settings-nav-btn-chevron {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    opacity: 0.55;
  }

  .settings-perf {
    margin-top: var(--srd-space-2);
  }

  .settings-perf sugar-fps-monitor {
    width: 100%;
    box-sizing: border-box;
  }

  @media (prefers-reduced-motion: reduce) {
    .settings-popup.active {
      animation: none;
    }
  }
`;
@customElement("settings-menu")
export class SettingsMenu extends LitElement {
  static override styles = settingsMenuCss;

  @property({ type: Boolean, reflect: true, attribute: "settings-open" })
  isOpen = false;

  /** Popup opens above/below the gear; `-start` = left-aligned (inward from left edge). */
  @property({ type: String, attribute: "popup-placement", reflect: true })
  popupPlacement: "above" | "above-start" | "below" | "below-end" = "above";

  /** Room Designer only: tips toggle + restart tour in the help section. */
  @property({ type: Boolean, reflect: true })
  tips = false;

  /** Room Designer only: shortcut into room lighting pane. */
  @property({ type: Boolean, reflect: true })
  lighting = false;

  /** Room Designer only: collision, OBB, and world bounding-box wires. */
  @property({ type: Boolean, reflect: true, attribute: "volume-debug" })
  volumeDebug = false;

  @property({ type: Array })
  private _hdrList: Array<{
    id: number;
    thumbnailUrl: string;
    url: string;
    fileUrl?: string;
    name?: string;
  }> = [];

  @property({ type: Number })
  private _selectedHdrId: number = 1;

  @property({ type: Boolean })
  private _tipsEnabled: boolean = TipStorage.isTipsEnabled();

  @property({ type: Boolean })
  private _devMode: boolean = isRoomDesignerDevMode();

  @property({ type: Boolean })
  private _advancedOpen = false;

  private _ignoreOutsideUntil = 0;

  private readonly _lengthUnitChanged = () => {
    this.requestUpdate();
  };

  private _handleLengthUnitChange(event: Event): void {
    const value = (event.currentTarget as HTMLSelectElement).value;
    if (value === "cm" || value === "mm" || value === "inch") {
      lengthUnitStore.setUnit(value);
    }
  }

  private readonly _renderSettingsChanged = () => {
    const storedUrl = renderSettingsStore.value.environmentUrl;
    const match = this._hdrList.find(
      (hdr) => (hdr.fileUrl ?? hdr.url) === storedUrl,
    );
    if (match) this._selectedHdrId = match.id;
    this.requestUpdate();
  };

  private readonly _onTipsEnabledChanged = (event: Event) => {
    const enabled = Boolean(
      (event as CustomEvent<{ enabled: boolean }>).detail?.enabled,
    );
    if (this._tipsEnabled === enabled) return;
    this._tipsEnabled = enabled;
  };

  async connectedCallback() {
    super.connectedCallback();
    document.addEventListener("pointerdown", this._handleClickOutside, true);
    window.addEventListener(
      TIPS_ENABLED_CHANGED_EVENT,
      this._onTipsEnabledChanged,
    );
    this._tipsEnabled = TipStorage.isTipsEnabled();
    renderSettingsStore.addEventListener(
      RENDER_SETTINGS_CHANGED_EVENT,
      this._renderSettingsChanged,
    );
    lengthUnitStore.addEventListener(
      LENGTH_UNIT_CHANGED_EVENT,
      this._lengthUnitChanged,
    );
    window.addEventListener(
      VOLUME_DEBUG_CHANGED_EVENT,
      this._onVolumeDebugChanged,
    );

    // HDR listesini yükle — seçimi shared store URL ile eşle; otomatik override yok.
    try {
      this._hdrList = await Api.fetchHDRS("sbalu");
      const storedUrl = renderSettingsStore.value.environmentUrl;
      const match = this._hdrList.find(
        (hdr: { fileUrl?: string; url?: string }) =>
          (hdr.fileUrl ?? hdr.url) === storedUrl,
      );
      this._selectedHdrId = match?.id ?? this._hdrList[0]?.id ?? 1;
    } catch (error) {
      logger.error("HDR listesi yüklenirken hata oluştu:", error);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("pointerdown", this._handleClickOutside, true);
    window.removeEventListener(
      TIPS_ENABLED_CHANGED_EVENT,
      this._onTipsEnabledChanged,
    );
    renderSettingsStore.removeEventListener(
      RENDER_SETTINGS_CHANGED_EVENT,
      this._renderSettingsChanged,
    );
    lengthUnitStore.removeEventListener(
      LENGTH_UNIT_CHANGED_EVENT,
      this._lengthUnitChanged,
    );
    window.removeEventListener(
      VOLUME_DEBUG_CHANGED_EVENT,
      this._onVolumeDebugChanged,
    );
  }

  private readonly _onVolumeDebugChanged = (): void => {
    this.requestUpdate();
  };

  private _setCollisionDebug(event: Event): void {
    setDragCollisionDebugEnabled(
      (event.currentTarget as HTMLInputElement).checked,
    );
  }

  private _setCornerLineSnap(event: Event): void {
    setCornerLineSnapEnabled(
      (event.currentTarget as HTMLInputElement).checked,
    );
  }

  private _setObbDebug(event: Event): void {
    setObbDebugDrawEnabled((event.currentTarget as HTMLInputElement).checked);
  }

  private _setAabbDebug(event: Event): void {
    setAabbDebugDrawEnabled((event.currentTarget as HTMLInputElement).checked);
  }

  private _setVolumeColor(role: VolumeDebugColorRole, event: Event): void {
    setVolumeDebugColor(role, (event.currentTarget as HTMLInputElement).value);
  }

  private _toggleSettings(event: Event) {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this._tipsEnabled = TipStorage.isTipsEnabled();
      this._ignoreOutsideUntil = performance.now() + 120;
    }
    this.dispatchEvent(
      new CustomEvent("settings-changed", {
        detail: { isOpen: this.isOpen },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _handleClickOutside = (event: Event) => {
    if (!this.isOpen || performance.now() < this._ignoreOutsideUntil) return;
    if (event.composedPath().includes(this)) return;
    this.isOpen = false;
  };

  private _handleTipsToggle(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this._tipsEnabled = checked;
    TipStorage.setTipsEnabled(checked);
    if (!checked) {
      TipStorage.markTourSeen("onboarding");
      TipStorage.setOnboardingReplay(false);
      TipManager.getInstance().stopAllTips();
    }
  }

  private _setDevMode(enabled: boolean) {
    if (this._devMode === enabled) return;
    this._devMode = enabled;
    setRoomDesignerDevMode(enabled);
    this.dispatchEvent(
      new CustomEvent(DEV_MODE_CHANGED_EVENT, {
        detail: { devMode: enabled },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _handleRestartTour() {
    this.isOpen = false;
    TipStorage.setTipsEnabled(true);
    this._tipsEnabled = true;
    TipManager.getInstance().restartTour();
  }

  private _handleOpenLighting() {
    this.isOpen = false;
    this.dispatchEvent(
      new CustomEvent("open-navigation-pane", {
        detail: { paneName: "lighting" },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _handleFullReset() {
    renderSettingsStore.reset();
  }

  private _qualityPreset(scale: number): "smooth" | "balanced" | "sharp" {
    if (scale <= 0.6) return "smooth";
    if (scale < 0.9) return "balanced";
    return "sharp";
  }

  private _applyQuality(event: Event): void {
    const preset = (event.currentTarget as HTMLSelectElement).value;
    const table = {
      smooth: { pixelRatioScale: 0.5, quality: "low" as const, msaa: false },
      balanced: { pixelRatioScale: 0.75, quality: "medium" as const, msaa: false },
      sharp: { pixelRatioScale: 1, quality: "high" as const, msaa: true },
    } as const;
    const next = table[preset as keyof typeof table] ?? table.balanced;
    renderSettingsStore.update({
      pixelRatioScale: next.pixelRatioScale,
      shadows: { quality: next.quality },
      aa: { fxaa: true, msaa: next.msaa, samples: next.msaa ? 4 : 1 },
    });
    this.renderRoot
      .querySelector<SugarFpsMonitor>("sugar-fps-monitor")
      ?.reset();
  }

  private _hdrLabel(hdr: { name?: string }, index: number): string {
    const raw = (hdr.name ?? "")
      .replace(/\.(hdr|exr|png|jpe?g|webp)$/i, "")
      .trim();
    const looksLikeFileId =
      !/\s/.test(raw) && /\d/.test(raw) && /^[a-z0-9_-]+$/i.test(raw);
    if (!raw || looksLikeFileId) {
      return `${i18next.t("environmentFallback")} ${index + 1}`;
    }
    return raw.replace(/[_-]+/g, " ");
  }

  private _handleHdrChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    const selectedHdr = this._hdrList.find(
      (hdr) => hdr.id === Number(select.value)
    );
    if (selectedHdr) {
      this._selectedHdrId = selectedHdr.id;
      const url = selectedHdr.fileUrl ?? selectedHdr.url;
      if (url) {
        renderSettingsStore.update({ environmentUrl: url });
      }
    }
  }

  private _number(event: Event): number {
    return Number.parseFloat((event.currentTarget as HTMLInputElement).value);
  }

  private _pixelRatioStats(scale: number): string {
    const dpr = window.devicePixelRatio || 1;
    const pixelRatio = renderSettingsStore.resolvePixelRatio(dpr);
    const engine = Engine.activeEngine;
    const canvas = engine?.canvas;
    const cssW = canvas?.clientWidth || engine?.width || 0;
    const cssH = canvas?.clientHeight || engine?.height || 0;
    const bufW = canvas?.width || Math.round(cssW * pixelRatio);
    const bufH = canvas?.height || Math.round(cssH * pixelRatio);
    const megapixels = (bufW * bufH) / 1e6;
    const fill = (pixelRatio / dpr) ** 2;
    return `DPR ${dpr.toFixed(2)} × ${scale.toFixed(2)} = ${pixelRatio.toFixed(2)} · ${cssW}×${cssH} → ${bufW}×${bufH} · ${megapixels.toFixed(2)} MP · ${(fill * 100).toFixed(0)}%`;
  }

  private _setScalar(
    key:
      | "exposure"
      | "environmentIntensity"
      | "aoMapIntensity"
      | "pixelRatioScale",
    event: Event,
  ): void {
    renderSettingsStore.update({ [key]: this._number(event) });
    if (key === "pixelRatioScale") {
      this.renderRoot
        .querySelector<SugarFpsMonitor>("sugar-fps-monitor")
        ?.reset();
      requestAnimationFrame(() => this.requestUpdate());
    }
  }

  private _setGtao(
    key: keyof RenderSettings["gtao"],
    value: number | boolean,
  ): void {
    renderSettingsStore.update({ gtao: { [key]: value } });
  }

  private _setSsgi(
    key: keyof RenderSettings["ssgi"],
    value: number | boolean | SsgiOutputMode,
  ): void {
    renderSettingsStore.update({ ssgi: { [key]: value } });
  }

  private _setSsgiSparse(
    key: keyof RenderSettings["ssgiSparse"],
    value:
      | number
      | boolean
      | SsgiSparseOutputMode
      | SsgiSparseEdgeKernel
      | SsgiSparseAdaptive
      | SsgiSparseVersion,
  ): void {
    renderSettingsStore.update({ ssgiSparse: { [key]: value } });
  }

  /** Seyrek GI v2'ye özel ayarlar (hücre, geçmiş, yumuşatma, kenar taraması, AO). */
  private _renderSparseGiV2Settings(renderSettings: RenderSettings) {
    const sparse = renderSettings.ssgiSparse;
    return html`
      <div class="settings-item">
        <span>${t("giStride")}</span>
        <select
          .value=${String(sparse.giStride)}
          @change=${(event: Event) =>
            this._setSsgiSparse(
              "giStride",
              Number((event.currentTarget as HTMLSelectElement).value),
            )}
        >
          <option value="2">2 × 2</option>
          <option value="4">4 × 4</option>
          <option value="6">6 × 6</option>
          <option value="8">8 × 8</option>
          <option value="16">16 × 16</option>
          <option value="32">32 × 32</option>
        </select>
      </div>
      ${this._range(
        t("giHistoryLength") as string,
        sparse.historyLength,
        1,
        64,
        1,
        (event) =>
          this._setSsgiSparse(
            "historyLength",
            Math.min(64, Math.max(1, Math.round(this._number(event)))),
          ),
      )}
      ${this._range(
        t("giDenoisePasses") as string,
        sparse.denoisePasses,
        0,
        3,
        1,
        (event) =>
          this._setSsgiSparse(
            "denoisePasses",
            Math.min(3, Math.max(0, Math.round(this._number(event)))),
          ),
      )}
      <div class="settings-item">
        <span>${t("giEdgeRefine")}</span>
        <input
          type="checkbox"
          .checked=${sparse.edgeRefine}
          @change=${(event: Event) =>
            this._setSsgiSparse(
              "edgeRefine",
              (event.currentTarget as HTMLInputElement).checked,
            )}
        />
      </div>
      ${this._range(
        t("aoIntensity") as string,
        sparse.aoIntensity,
        0,
        4,
        0.01,
        (event) => this._setSsgiSparse("aoIntensity", this._number(event)),
      )}
    `;
  }

  private _setTaa(
    key: keyof RenderSettings["taa"],
    value: number | boolean,
  ): void {
    renderSettingsStore.update({ taa: { [key]: value } });
  }

  private _setAa(
    key: keyof RenderSettings["aa"],
    value: number | boolean,
  ): void {
    renderSettingsStore.update({ aa: { [key]: value } });
  }

  private _setPathTraceFrameCount(event: Event): void {
    renderSettingsStore.update({
      pathTrace: { frameCount: Math.round(this._number(event)) },
    });
  }

  private _range(
    label: string,
    value: number,
    min: number,
    max: number,
    step: number,
    onInput: (event: Event) => void,
    decimals?: number,
  ) {
    const places = decimals ?? (step < 0.05 ? 2 : step < 1 ? 1 : 0);
    return html`
      <label class="settings-range">
        <span class="settings-range-meta">
          <span>${label}</span>
          <output>${value.toFixed(places)}</output>
        </span>
        <input
          type="range"
          min=${min}
          max=${max}
          step=${step}
          .value=${String(value)}
          @input=${onInput}
        />
      </label>
    `;
  }

  render() {
    const renderSettings = renderSettingsStore.value;
    return html`
      <button
        type="button"
        class="settings-button"
        aria-label=${t("settings")}
        aria-expanded=${this.isOpen ? "true" : "false"}
        @click=${this._toggleSettings}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="3"></circle>
          <path
            d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"
          ></path>
        </svg>
      </button>

      <!-- Settings Popup -->
      <div class="settings-popup ${this.isOpen ? "active" : ""}">
        <h3>${t("settings")}</h3>
        <div class="settings-popup-content">
          <section class="settings-group settings-group-display">
          <div class="settings-section-title">${t("settingsSectionDisplay")}</div>
          <div class="settings-item">
            <span>${t("lengthUnit")}</span>
            <select
              .value=${getLengthUnit()}
              @change=${this._handleLengthUnitChange}
            >
              <option value="cm">${t("lengthUnitCm")}</option>
              <option value="mm">${t("lengthUnitMm")}</option>
              <option value="inch">${t("lengthUnitInch")}</option>
            </select>
          </div>
          <div class="settings-item">
            <span>${t("imageQuality")}</span>
            <select
              .value=${this._qualityPreset(renderSettings.pixelRatioScale)}
              @change=${this._applyQuality}
            >
              <option value="smooth">${t("qualitySmooth")}</option>
              <option value="balanced">${t("qualityBalanced")}</option>
              <option value="sharp">${t("qualitySharp")}</option>
            </select>
          </div>
          <p class="settings-hint">
            ${Math.round(renderSettings.pixelRatioScale * 100)}% · ${t("clarityHint")}
          </p>
          </section>

          <section class="settings-group settings-group-environment">
          <div class="settings-section-title">${t("settingsSectionEnvironment")}</div>
          <div class="settings-item">
            <span>${t("hdrSelection")}</span>
            <select @change=${this._handleHdrChange}>
              ${this._hdrList.map(
                (hdr, index) => html`
                  <option
                    value=${hdr.id}
                    ?selected=${this._selectedHdrId === hdr.id}
                  >
                    ${this._hdrLabel(hdr, index)}
                  </option>
                `,
              )}
            </select>
          </div>
          ${(() => {
            const selected = this._hdrList.find(
              (hdr) => hdr.id === this._selectedHdrId,
            );
            const thumb = selected?.thumbnailUrl;
            return thumb
              ? html`<img
                  class="settings-hdr-preview"
                  src=${thumb}
                  alt=${t("hdrPreview")}
                />`
              : nothing;
          })()}
          ${this._range(
            t("environmentLight") as string,
            renderSettings.environmentIntensity,
            0,
            2,
            0.01,
            (event) => this._setScalar("environmentIntensity", event),
          )}
          <div class="settings-item">
            <span>${t("roomLights")}</span>
            <input
              type="checkbox"
              .checked=${renderSettings.lights.enabled}
              @change=${(event: Event) =>
                renderSettingsStore.update({
                  lights: {
                    enabled: (event.currentTarget as HTMLInputElement).checked,
                  },
                })}
            />
          </div>
          <div class="settings-item">
            <span>${t("shadows")}</span>
            <input
              type="checkbox"
              .checked=${renderSettings.shadows.enabled}
              @change=${(event: Event) =>
                renderSettingsStore.update({
                  shadows: {
                    enabled: (event.currentTarget as HTMLInputElement).checked,
                  },
                })}
            />
          </div>
          ${this._range(
            t("exposure") as string,
            renderSettings.exposure,
            0.1,
            4,
            0.05,
            (event) => this._setScalar("exposure", event),
          )}
          </section>

          ${this.lighting
            ? html`
                <button
                  type="button"
                  class="settings-nav-btn"
                  @click=${this._handleOpenLighting}
                >
                  <svg
                    class="settings-nav-btn-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M9 18h6" />
                    <path d="M10 22h4" />
                    <path d="M12 2v1" />
                    <path d="M5.6 5.6l.7.7" />
                    <path d="M18.4 5.6l-.7.7" />
                    <path d="M3 12h1" />
                    <path d="M20 12h1" />
                    <path
                      d="M8 12a4 4 0 1 1 8 0c0 1.5-.8 2.4-1.5 3.1S13.5 16.5 13.5 18h-3c0-1.5-.8-2.2-1.5-2.9S8 13.5 8 12z"
                    />
                  </svg>
                  <span class="settings-nav-btn-label">${t("editLights")}</span>
                  <svg
                    class="settings-nav-btn-chevron"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              `
            : nothing}

          <button
            type="button"
            class="settings-advanced-toggle"
            aria-expanded=${this._advancedOpen ? "true" : "false"}
            @click=${() => {
              this._advancedOpen = !this._advancedOpen;
            }}
          >
            <span>${t("advancedDisplay")}</span>
            <span aria-hidden="true">${this._advancedOpen ? "▾" : "▸"}</span>
          </button>
          ${this._advancedOpen
            ? html`
          <section class="settings-group settings-group-advanced">
          ${this._range(
            t("pixelRatioScale") as string,
            renderSettings.pixelRatioScale,
            0.25,
            1,
            0.05,
            (event) => this._setScalar("pixelRatioScale", event),
            2,
          )}
          <p class="settings-hint">${t("pixelRatioScaleHint")}</p>
          <p class="settings-hint">${this._pixelRatioStats(renderSettings.pixelRatioScale)}</p>
          ${this.isOpen
            ? html`
                <div class="settings-perf">
                  <sugar-fps-monitor
                    position="none"
                    detailed
                    update-interval="250"
                  ></sugar-fps-monitor>
                </div>
              `
            : nothing}
          <div class="settings-item">
            <span>${t("shadowQuality")}</span>
            <select
              .value=${renderSettings.shadows.quality}
              @change=${(event: Event) =>
                renderSettingsStore.update({
                  shadows: {
                    quality: (event.currentTarget as HTMLSelectElement)
                      .value as ShadowQuality,
                  },
                })}
            >
              <option value="low">${t("qualityLow")}</option>
              <option value="medium">${t("qualityMedium")}</option>
              <option value="high">${t("qualityHigh")}</option>
            </select>
          </div>

          <section class="settings-group settings-group-nested">
          <div class="settings-section-title">${t("settingsSectionColor")}</div>
          <div class="settings-item">
            <span>${t("toneMapping")}</span>
            <select
              .value=${renderSettings.toneMapping}
              @change=${(event: Event) =>
                renderSettingsStore.update({
                  toneMapping: (event.currentTarget as HTMLSelectElement)
                    .value as ToneMappingName,
                })}
            >
              <option value="none">${t("disabled")}</option>
              <option value="linear">Linear</option>
              <option value="reinhard">Reinhard</option>
              <option value="cineon">Cineon</option>
              <option value="aces">ACES Filmic</option>
            </select>
          </div>
          ${this._range(
            t("aoMapIntensity") as string,
            renderSettings.aoMapIntensity,
            0,
            3,
            0.01,
            (event) => this._setScalar("aoMapIntensity", event),
          )}
          </section>

          <section class="settings-group settings-group-nested">
          <div class="settings-section-title">${t("antialias")}</div>
          <div class="settings-item">
            <span>${t("fxaa")}</span>
            <input
              type="checkbox"
              .checked=${renderSettings.aa.fxaa}
              @change=${(event: Event) =>
                this._setAa(
                  "fxaa",
                  (event.currentTarget as HTMLInputElement).checked,
                )}
            />
          </div>
          <div class="settings-item">
            <span>${t("msaa")}</span>
            <input
              type="checkbox"
              .checked=${renderSettings.aa.msaa}
              @change=${(event: Event) =>
                this._setAa(
                  "msaa",
                  (event.currentTarget as HTMLInputElement).checked,
                )}
            />
          </div>
          ${renderSettings.aa.msaa
            ? this._range(
                t("msaaSamples") as string,
                renderSettings.aa.samples,
                1,
                8,
                1,
                (event) =>
                  this._setAa("samples", Math.round(this._number(event))),
              )
            : nothing}

          <div class="settings-section-title">TAA</div>
          <div
            class="settings-item ${renderSettings.pathTrace.enabled
              ? "is-disabled"
              : ""}"
          >
            <span>TAA</span>
            <input
              type="checkbox"
              .checked=${renderSettings.taa.enabled}
              ?disabled=${renderSettings.pathTrace.enabled}
              @change=${(event: Event) =>
                this._setTaa(
                  "enabled",
                  (event.currentTarget as HTMLInputElement).checked,
                )}
            />
          </div>
          ${this._range(
            t("sampleLevel") as string,
            renderSettings.taa.sampleLevel,
            0,
            5,
            1,
            (event) =>
              this._setTaa("sampleLevel", Math.round(this._number(event))),
          )}
          <div class="settings-item">
            <span>${t("unbiased")}</span>
            <input
              type="checkbox"
              .checked=${renderSettings.taa.unbiased}
              @change=${(event: Event) =>
                this._setTaa(
                  "unbiased",
                  (event.currentTarget as HTMLInputElement).checked,
                )}
            />
          </div>

          </section>

          <section class="settings-group settings-group-nested">
          <div class="settings-section-title">${t("ambientOcclusion")}</div>

          <div
            class="settings-block ${renderSettings.ssgi.enabled
              ? "is-active"
              : ""} ${renderSettings.pathTrace.enabled ||
            renderSettings.ssgiSparse.enabled
              ? "is-disabled"
              : ""}"
          >
            <div class="settings-block-header">
              <span class="settings-block-title">${t("ssgi")}</span>
              <input
                type="checkbox"
                .checked=${renderSettings.ssgi.enabled}
                ?disabled=${renderSettings.pathTrace.enabled ||
                renderSettings.ssgiSparse.enabled}
                @change=${(event: Event) =>
                  this._setSsgi(
                    "enabled",
                    (event.currentTarget as HTMLInputElement).checked,
                  )}
              />
            </div>
            <p class="settings-hint">${t("ssgiHint")}</p>
            ${renderSettings.ssgi.enabled
              ? html`
                  <div class="settings-item">
                    <span>${t("ssgiOutput")}</span>
                    <select
                      .value=${renderSettings.ssgi.output}
                      @change=${(event: Event) =>
                        this._setSsgi(
                          "output",
                          (event.currentTarget as HTMLSelectElement)
                            .value as SsgiOutputMode,
                        )}
                    >
                      <option value="combined">${t("ssgiOutputCombined")}</option>
                      <option value="direct">${t("ssgiOutputDirect")}</option>
                      <option value="ao">${t("ssgiOutputAo")}</option>
                      <option value="gi">${t("ssgiOutputGi")}</option>
                      <option value="normal">${t("ssgiOutputNormal")}</option>
                      <option value="depth">${t("ssgiOutputDepth")}</option>
                    </select>
                  </div>
                  ${this._range(
                    t("sliceCount") as string,
                    renderSettings.ssgi.sliceCount,
                    1,
                    4,
                    1,
                    (event) =>
                      this._setSsgi(
                        "sliceCount",
                        Math.round(this._number(event)),
                      ),
                  )}
                  ${this._range(
                    t("stepCount") as string,
                    renderSettings.ssgi.stepCount,
                    1,
                    32,
                    1,
                    (event) =>
                      this._setSsgi(
                        "stepCount",
                        Math.round(this._number(event)),
                      ),
                  )}
                  ${this._range(
                    t("radius") as string,
                    renderSettings.ssgi.radius,
                    1,
                    25,
                    0.01,
                    (event) => this._setSsgi("radius", this._number(event)),
                  )}
                  ${this._range(
                    t("expFactor") as string,
                    renderSettings.ssgi.expFactor,
                    1,
                    3,
                    0.01,
                    (event) => this._setSsgi("expFactor", this._number(event)),
                  )}
                  ${this._range(
                    t("thickness") as string,
                    renderSettings.ssgi.thickness,
                    0.01,
                    10,
                    0.01,
                    (event) => this._setSsgi("thickness", this._number(event)),
                  )}
                  ${this._range(
                    t("backfaceLighting") as string,
                    renderSettings.ssgi.backfaceLighting,
                    0,
                    1,
                    0.01,
                    (event) =>
                      this._setSsgi("backfaceLighting", this._number(event)),
                  )}
                  ${this._range(
                    t("aoIntensity") as string,
                    renderSettings.ssgi.aoIntensity,
                    0,
                    4,
                    0.01,
                    (event) =>
                      this._setSsgi("aoIntensity", this._number(event)),
                  )}
                  ${this._range(
                    t("giIntensity") as string,
                    renderSettings.ssgi.giIntensity,
                    0,
                    100,
                    0.01,
                    (event) =>
                      this._setSsgi("giIntensity", this._number(event)),
                  )}
                  <div class="settings-item">
                    <span>${t("useLinearThickness")}</span>
                    <input
                      type="checkbox"
                      .checked=${renderSettings.ssgi.useLinearThickness}
                      @change=${(event: Event) =>
                        this._setSsgi(
                          "useLinearThickness",
                          (event.currentTarget as HTMLInputElement).checked,
                        )}
                    />
                  </div>
                  <div class="settings-item">
                    <span>${t("useScreenSpaceSampling")}</span>
                    <input
                      type="checkbox"
                      .checked=${renderSettings.ssgi.useScreenSpaceSampling}
                      @change=${(event: Event) =>
                        this._setSsgi(
                          "useScreenSpaceSampling",
                          (event.currentTarget as HTMLInputElement).checked,
                        )}
                    />
                  </div>
                  <div class="settings-item">
                    <span>${t("useTemporalFiltering")}</span>
                    <input
                      type="checkbox"
                      .checked=${renderSettings.ssgi.useTemporalFiltering}
                      @change=${(event: Event) =>
                        this._setSsgi(
                          "useTemporalFiltering",
                          (event.currentTarget as HTMLInputElement).checked,
                        )}
                    />
                  </div>
                `
              : ""}
          </div>

          <div
            class="settings-block ${renderSettings.ssgiSparse.enabled
              ? "is-active"
              : ""} ${renderSettings.pathTrace.enabled ||
            renderSettings.ssgi.enabled
              ? "is-disabled"
              : ""}"
          >
            <div class="settings-block-header">
              <span class="settings-block-title">${t("ssgiSparse")}</span>
              <input
                type="checkbox"
                .checked=${renderSettings.ssgiSparse.enabled}
                ?disabled=${renderSettings.pathTrace.enabled ||
                renderSettings.ssgi.enabled}
                @change=${(event: Event) =>
                  this._setSsgiSparse(
                    "enabled",
                    (event.currentTarget as HTMLInputElement).checked,
                  )}
              />
            </div>
            <p class="settings-hint">
              ${t(
                renderSettings.ssgiSparse.version === "v2"
                  ? "ssgiSparseV2Hint"
                  : "ssgiSparseHint",
              )}
            </p>
            ${renderSettings.ssgiSparse.enabled
              ? html`
                  <div class="settings-item">
                    <span>${t("ssgiSparseVersion")}</span>
                    <select
                      .value=${renderSettings.ssgiSparse.version}
                      @change=${(event: Event) =>
                        this._setSsgiSparse(
                          "version",
                          (event.currentTarget as HTMLSelectElement)
                            .value as SsgiSparseVersion,
                        )}
                    >
                      <option value="v1">${t("ssgiSparseVersionV1")}</option>
                      <option value="v2">${t("ssgiSparseVersionV2")}</option>
                    </select>
                  </div>
                  <div class="settings-item">
                    <span>${t("ssgiOutput")}</span>
                    <select
                      .value=${renderSettings.ssgiSparse.output}
                      @change=${(event: Event) =>
                        this._setSsgiSparse(
                          "output",
                          (event.currentTarget as HTMLSelectElement)
                            .value as SsgiSparseOutputMode,
                        )}
                    >
                      <option value="combined">${t("ssgiOutputCombined")}</option>
                      <option value="direct">${t("ssgiOutputDirect")}</option>
                      <option value="gi">${t("ssgiOutputGi")}</option>
                      ${renderSettings.ssgiSparse.version === "v2"
                        ? html`
                            <option value="ao">${t("ssgiOutputAo")}</option>
                            <option value="source">${t("ssgiOutputGiSource")}</option>
                          `
                        : html`
                            <option value="normal">${t("ssgiOutputNormal")}</option>
                            <option value="depth">${t("ssgiOutputDepth")}</option>
                          `}
                      <option value="debug">${t("ssgiOutputGiDebug")}</option>
                    </select>
                  </div>
                  ${renderSettings.ssgiSparse.version === "v2"
                    ? this._renderSparseGiV2Settings(renderSettings)
                    : nothing}
                  ${renderSettings.ssgiSparse.version === "v1"
                    ? html`
                  ${this._range(
                    t("giStride") as string,
                    renderSettings.ssgiSparse.giStride,
                    2,
                    64,
                    1,
                    (event) =>
                      this._setSsgiSparse(
                        "giStride",
                        Math.min(64, Math.max(2, Math.round(this._number(event)))),
                      ),
                  )}
                  <div class="settings-item">
                    <span>${t("giAdaptive")}</span>
                    <select
                      .value=${renderSettings.ssgiSparse.giAdaptive}
                      @change=${(event: Event) =>
                        this._setSsgiSparse(
                          "giAdaptive",
                          (event.currentTarget as HTMLSelectElement)
                            .value as SsgiSparseAdaptive,
                        )}
                    >
                      <option value="off">${t("giAdaptiveOff")}</option>
                      <option value="edge">${t("giAdaptiveEdge")}</option>
                      <option value="refine">${t("giAdaptiveRefine")}</option>
                    </select>
                  </div>
                  ${renderSettings.ssgiSparse.giAdaptive === "edge"
                    ? html`
                  ${this._range(
                    t("giEdgeThickness") as string,
                    renderSettings.ssgiSparse.giEdgeThickness,
                    0,
                    8,
                    1,
                    (event) =>
                      this._setSsgiSparse(
                        "giEdgeThickness",
                        Math.min(8, Math.max(0, Math.round(this._number(event)))),
                      ),
                  )}
                  <div class="settings-item">
                    <span>${t("giEdgeKernel")}</span>
                    <select
                      .value=${renderSettings.ssgiSparse.giEdgeKernel}
                      @change=${(event: Event) =>
                        this._setSsgiSparse(
                          "giEdgeKernel",
                          (event.currentTarget as HTMLSelectElement)
                            .value as SsgiSparseEdgeKernel,
                        )}
                    >
                      <option value="cross">${t("giEdgeKernelCross")}</option>
                      <option value="sobel">${t("giEdgeKernelSobel")}</option>
                      <option value="prewitt">${t("giEdgeKernelPrewitt")}</option>
                      <option value="scharr">${t("giEdgeKernelScharr")}</option>
                    </select>
                  </div>
                  ${this._range(
                    t("giEdgeThreshold") as string,
                    renderSettings.ssgiSparse.giEdgeThreshold,
                    0.001,
                    0.5,
                    0.001,
                    (event) =>
                      this._setSsgiSparse(
                        "giEdgeThreshold",
                        this._number(event),
                      ),
                  )}
                    `
                    : nothing}
                  ${renderSettings.ssgiSparse.giAdaptive === "refine"
                    ? html`<p class="settings-hint">${t("giAdaptiveRefineHint")}</p>`
                    : nothing}
                  ${this._range(
                    t("giDepthThreshold") as string,
                    renderSettings.ssgiSparse.giDepthThreshold,
                    0.01,
                    2,
                    0.01,
                    (event) =>
                      this._setSsgiSparse(
                        "giDepthThreshold",
                        this._number(event),
                      ),
                  )}
                  ${this._range(
                    t("giNormalThreshold") as string,
                    renderSettings.ssgiSparse.giNormalThreshold,
                    0,
                    1,
                    0.01,
                    (event) =>
                      this._setSsgiSparse(
                        "giNormalThreshold",
                        this._number(event),
                      ),
                  )}
                  ${this._range(
                    t("giMinOkCount") as string,
                    renderSettings.ssgiSparse.giMinOkCount,
                    1,
                    4,
                    1,
                    (event) =>
                      this._setSsgiSparse(
                        "giMinOkCount",
                        Math.min(4, Math.max(1, Math.round(this._number(event)))),
                      ),
                  )}
                    `
                    : nothing}
                  ${this._range(
                    t("sliceCount") as string,
                    renderSettings.ssgiSparse.sliceCount,
                    1,
                    4,
                    1,
                    (event) =>
                      this._setSsgiSparse(
                        "sliceCount",
                        Math.round(this._number(event)),
                      ),
                  )}
                  ${this._range(
                    t("stepCount") as string,
                    renderSettings.ssgiSparse.stepCount,
                    1,
                    32,
                    1,
                    (event) =>
                      this._setSsgiSparse(
                        "stepCount",
                        Math.round(this._number(event)),
                      ),
                  )}
                  ${this._range(
                    t("radius") as string,
                    renderSettings.ssgiSparse.radius,
                    1,
                    25,
                    0.01,
                    (event) =>
                      this._setSsgiSparse("radius", this._number(event)),
                  )}
                  ${this._range(
                    t("expFactor") as string,
                    renderSettings.ssgiSparse.expFactor,
                    1,
                    3,
                    0.01,
                    (event) =>
                      this._setSsgiSparse("expFactor", this._number(event)),
                  )}
                  ${this._range(
                    t("thickness") as string,
                    renderSettings.ssgiSparse.thickness,
                    0.01,
                    10,
                    0.01,
                    (event) =>
                      this._setSsgiSparse("thickness", this._number(event)),
                  )}
                  ${this._range(
                    t("backfaceLighting") as string,
                    renderSettings.ssgiSparse.backfaceLighting,
                    0,
                    1,
                    0.01,
                    (event) =>
                      this._setSsgiSparse(
                        "backfaceLighting",
                        this._number(event),
                      ),
                  )}
                  ${this._range(
                    t("giIntensity") as string,
                    renderSettings.ssgiSparse.giIntensity,
                    0,
                    100,
                    0.01,
                    (event) =>
                      this._setSsgiSparse("giIntensity", this._number(event)),
                  )}
                  <div class="settings-item">
                    <span>${t("useLinearThickness")}</span>
                    <input
                      type="checkbox"
                      .checked=${renderSettings.ssgiSparse.useLinearThickness}
                      @change=${(event: Event) =>
                        this._setSsgiSparse(
                          "useLinearThickness",
                          (event.currentTarget as HTMLInputElement).checked,
                        )}
                    />
                  </div>
                  <div class="settings-item">
                    <span>${t("useScreenSpaceSampling")}</span>
                    <input
                      type="checkbox"
                      .checked=${
                        renderSettings.ssgiSparse.useScreenSpaceSampling
                      }
                      @change=${(event: Event) =>
                        this._setSsgiSparse(
                          "useScreenSpaceSampling",
                          (event.currentTarget as HTMLInputElement).checked,
                        )}
                    />
                  </div>
                  ${renderSettings.ssgiSparse.version === "v1"
                    ? html`
                  <div class="settings-item">
                    <span>${t("useTemporalFiltering")}</span>
                    <input
                      type="checkbox"
                      .checked=${
                        renderSettings.ssgiSparse.useTemporalFiltering
                      }
                      @change=${(event: Event) =>
                        this._setSsgiSparse(
                          "useTemporalFiltering",
                          (event.currentTarget as HTMLInputElement).checked,
                        )}
                    />
                  </div>
                    `
                    : nothing}
                `
              : ""}
          </div>

          <div
            class="settings-block ${renderSettings.gtao.enabled
              ? "is-active"
              : ""} ${renderSettings.ssgi.enabled ||
            renderSettings.ssgiSparse.enabled ||
            renderSettings.pathTrace.enabled
              ? "is-disabled"
              : ""}"
          >
            <div class="settings-block-header">
              <span class="settings-block-title">GTAO</span>
              <input
                type="checkbox"
                .checked=${renderSettings.gtao.enabled}
                ?disabled=${renderSettings.ssgi.enabled ||
                renderSettings.ssgiSparse.enabled ||
                renderSettings.pathTrace.enabled}
                @change=${(event: Event) =>
                  this._setGtao(
                    "enabled",
                    (event.currentTarget as HTMLInputElement).checked,
                  )}
              />
            </div>
            <p class="settings-hint">${t("gtaoHint")}</p>
            ${!renderSettings.ssgi.enabled &&
            !renderSettings.ssgiSparse.enabled &&
            !renderSettings.pathTrace.enabled
              ? html`
                  ${this._range(
                    t("intensity") as string,
                    renderSettings.gtao.intensity,
                    0,
                    3,
                    0.05,
                    (event) => this._setGtao("intensity", this._number(event)),
                  )}
                  ${this._range(
                    t("radius") as string,
                    renderSettings.gtao.radius,
                    0.02,
                    1,
                    0.01,
                    (event) => this._setGtao("radius", this._number(event)),
                  )}
                  ${this._range(
                    t("gtaoSamples") as string,
                    renderSettings.gtao.samples,
                    4,
                    32,
                    1,
                    (event) =>
                      this._setGtao(
                        "samples",
                        Math.round(this._number(event)),
                      ),
                  )}
                  ${this._range(
                    t("gtaoScale") as string,
                    renderSettings.gtao.scale,
                    0.1,
                    3,
                    0.05,
                    (event) => this._setGtao("scale", this._number(event)),
                  )}
                  ${this._range(
                    t("thickness") as string,
                    renderSettings.gtao.thickness,
                    0.01,
                    2,
                    0.01,
                    (event) => this._setGtao("thickness", this._number(event)),
                  )}
                  ${this._range(
                    t("distanceExponent") as string,
                    renderSettings.gtao.distanceExponent,
                    0.1,
                    4,
                    0.1,
                    (event) =>
                      this._setGtao("distanceExponent", this._number(event)),
                  )}
                  ${this._range(
                    t("distanceFallOff") as string,
                    renderSettings.gtao.distanceFallOff,
                    0,
                    2,
                    0.05,
                    (event) =>
                      this._setGtao("distanceFallOff", this._number(event)),
                  )}
                  ${this._range(
                    t("resolutionScale") as string,
                    renderSettings.gtao.resolutionScale,
                    0.25,
                    1,
                    0.05,
                    (event) =>
                      this._setGtao("resolutionScale", this._number(event)),
                  )}
                  <div class="settings-item">
                    <span>${t("useTemporalFiltering")}</span>
                    <input
                      type="checkbox"
                      .checked=${renderSettings.gtao.useTemporalFiltering}
                      @change=${(event: Event) =>
                        this._setGtao(
                          "useTemporalFiltering",
                          (event.currentTarget as HTMLInputElement).checked,
                        )}
                    />
                  </div>
                  <div class="settings-item">
                    <span>${t("gtaoDenoise")}</span>
                    <input
                      type="checkbox"
                      .checked=${renderSettings.gtao.denoise}
                      @change=${(event: Event) =>
                        this._setGtao(
                          "denoise",
                          (event.currentTarget as HTMLInputElement).checked,
                        )}
                    />
                  </div>
                  ${renderSettings.gtao.denoise
                    ? html`
                        ${this._range(
                          t("gtaoLumaPhi") as string,
                          renderSettings.gtao.lumaPhi,
                          1,
                          20,
                          0.5,
                          (event) =>
                            this._setGtao("lumaPhi", this._number(event)),
                        )}
                        ${this._range(
                          t("gtaoDepthPhi") as string,
                          renderSettings.gtao.depthPhi,
                          0.1,
                          10,
                          0.1,
                          (event) =>
                            this._setGtao("depthPhi", this._number(event)),
                        )}
                        ${this._range(
                          t("gtaoNormalPhi") as string,
                          renderSettings.gtao.normalPhi,
                          0.1,
                          10,
                          0.1,
                          (event) =>
                            this._setGtao("normalPhi", this._number(event)),
                        )}
                        ${this._range(
                          t("gtaoPdRadius") as string,
                          renderSettings.gtao.pdRadius,
                          1,
                          16,
                          1,
                          (event) =>
                            this._setGtao(
                              "pdRadius",
                              Math.round(this._number(event)),
                            ),
                        )}
                      `
                    : ""}
                `
              : ""}
          </div>
          </section>

          <section class="settings-group settings-group-nested">
          <div class="settings-section-title">${t("pathTraceRender")}</div>
          ${this._range(
            t("pathTraceFrameCount") as string,
            renderSettings.pathTrace.frameCount,
            16,
            512,
            16,
            (event) => this._setPathTraceFrameCount(event),
          )}

          <div class="settings-actions">
            <button
              type="button"
              class="settings-reset-btn"
              @click=${this._handleFullReset}
            >
              ${t("fullReset")}
            </button>
          </div>
          </section>
          </section>
            `
            : nothing}

          ${this.volumeDebug
            ? html`
                <section class="settings-group settings-group-debug">
                <div class="settings-section-title">${t("volumeDebug")}</div>
                <p class="settings-hint">${t("volumeDebugHint")}</p>
                <div class="settings-item">
                  <span>${t("cornerLineSnap")}</span>
                  <input
                    type="checkbox"
                    .checked=${isCornerLineSnapEnabled()}
                    @change=${this._setCornerLineSnap}
                  />
                </div>
                <div class="settings-item">
                  <span>${t("volumeDebugCollision")}</span>
                  <span class="settings-volume-controls">
                    <input
                      type="color"
                      aria-label=${t("volumeDebugColorContact")}
                      .value=${volumeDebugColorHex("contact")}
                      @change=${(event: Event) =>
                        this._setVolumeColor("contact", event)}
                    />
                    <input
                      type="checkbox"
                      .checked=${isDragCollisionDebugEnabled()}
                      ?disabled=${isDragCollisionDebugUrlForced()}
                      @change=${this._setCollisionDebug}
                    />
                  </span>
                </div>
                <div class="settings-item">
                  <span>${t("volumeDebugObb")}</span>
                  <span class="settings-volume-controls">
                    <input
                      type="color"
                      aria-label=${t("volumeDebugColorParts")}
                      .value=${volumeDebugColorHex("parts")}
                      @change=${(event: Event) =>
                        this._setVolumeColor("parts", event)}
                    />
                    <input
                      type="color"
                      aria-label=${t("volumeDebugColorHull")}
                      .value=${volumeDebugColorHex("hull")}
                      @change=${(event: Event) =>
                        this._setVolumeColor("hull", event)}
                    />
                    <input
                      type="checkbox"
                      .checked=${isObbDebugDrawEnabled()}
                      ?disabled=${isObbDebugUrlForced()}
                      @change=${this._setObbDebug}
                    />
                  </span>
                </div>
                <div class="settings-item">
                  <span>${t("volumeDebugAabb")}</span>
                  <span class="settings-volume-controls">
                    <input
                      type="color"
                      aria-label=${t("volumeDebugColorAabb")}
                      .value=${volumeDebugColorHex("aabb")}
                      @change=${(event: Event) =>
                        this._setVolumeColor("aabb", event)}
                    />
                    <input
                      type="checkbox"
                      .checked=${isAabbDebugDrawEnabled()}
                      ?disabled=${isAabbDebugUrlForced()}
                      @change=${this._setAabbDebug}
                    />
                  </span>
                </div>
                </section>
              `
            : nothing}

          ${this.tips
            ? html`
                <section class="settings-group settings-group-help">
                <div class="settings-section-title">${t("help")}</div>
                <div class="settings-item">
                  <span>${t("appMode")}</span>
                  <div
                    class="settings-segment"
                    role="group"
                    aria-label=${t("appMode")}
                  >
                    <button
                      type="button"
                      class="settings-segment-btn ${this._devMode
                        ? ""
                        : "active"}"
                      aria-pressed=${this._devMode ? "false" : "true"}
                      @click=${() => this._setDevMode(false)}
                    >
                      ${t("liveMode")}
                    </button>
                    <button
                      type="button"
                      class="settings-segment-btn ${this._devMode
                        ? "active"
                        : ""}"
                      aria-pressed=${this._devMode ? "true" : "false"}
                      @click=${() => this._setDevMode(true)}
                    >
                      ${t("devMode")}
                    </button>
                  </div>
                </div>
                <div class="settings-item">
                  <span>${t("usageTips")}</span>
                  <input
                    type="checkbox"
                    .checked=${this._tipsEnabled}
                    @change=${this._handleTipsToggle}
                  />
                </div>
                <div class="settings-item">
                  <span>${t("restartTour")}</span>
                  <button
                    class="settings-restart-btn"
                    @click=${this._handleRestartTour}
                  >
                    ${t("start")}
                  </button>
                </div>
                </section>
              `
            : nothing}
        </div>
      </div>
    `;
  }
}
